import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import OrderSellLayout from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellLayout'
import { ComponentWithPermissions } from '../../../../../../../../../baseComponents/CreateRoutering/types'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { InputTableURLsTable } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { getAllWorkingCenters, getUserPermissions } from '../../../../../../../../../utils/reducers/getters'
import { dictionaryComplexFromJsonArr } from '../../../../../../../../../utils/globals/dictionary'
import { ColumnOrderTableSchema } from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import { permissionCheck } from '../../../../../../../../../baseComponents/PermissionChecker/function'
import { CustomRenderBoolean, CustomRenderString } from '../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ITDiscountDisplay, ITTypeQuantityDisplay } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Displays'
import { ProductExtendedTyInputTableListing } from '../../../../../../../../../specificComponents/Product/types'
import useMultiOrderScreenLogic from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/useMultiOrderScreenLogic'
import { sellInvoiceStatusInversed } from '@teinor/erp/types/company/order/sell/sellInvoice'
import useSellOrderHeaderUrls from '../../../../../../../../../utils/hooks/sells/orders/useSellOrderHeaderUrls'
import { SellOrderProductModelTypeExtended } from '@teinor/erp/types/company/order/sell/sellOrder/sellOrderProduct'
import locations from '../../../../../../../../../utils/globals/locations'
import MultiSellHeaderConnect from '../../../../../../../../../specificComponents/sells/components/multi/MultiSellHeader'
import { MultiSellButtonWithStock } from '../../../../../../../../../specificComponents/sells/components/multi/MultiSellButton'

type CreateMultiSellDeliveryNotesProps = ComponentWithPermissions & {
}

const CreateMultiSellDeliveryNotes: React.FC<CreateMultiSellDeliveryNotesProps> = ({ userPermissions }) => {
    const { t } = useTwinTranslation()
    const { ids } = useParams()
    const parseIds = ids?.split('-')
    const inputTableURLs: InputTableURLsTable = {
        createData: ['', {}],
        getData: ['/api/app/order/sell/sellOrder/getMultiSellOrderComplete', { id: parseIds || [] }],
        updateData: '',
        updateHeaderData: '',
        getSubProducts: ['/api/app/order/sell/sellOrder/orderProduct/getAllSubSellOrderProducts', ['CustomerId']],
        updateOrderLines: '',
        delete: '',
        printTags: '/api/app/order/sell/sellOrder/printSellOrderTags'
    }
    const orderHeaderURLs = useSellOrderHeaderUrls()

    const { completeData, setCompleteData, getCompleteData, tableConfiguration, setTableConfiguration, conflicts, setConflicts } = useMultiOrderScreenLogic({ inputTableURLs, userPermissions, type: 'sellOrderHeader', billingType: 'BillingTypeSellOptions' })
    const workingCenters = getAllWorkingCenters()
    const dictWarehouses = workingCenters?.[completeData?.header?.WorkingCenter?.id]?.dictWarehouses
    const accounts = useMemo(() => dictionaryComplexFromJsonArr(completeData?.header?.OrderAccounts), [completeData?.header?.OrderAccounts])
    if(!completeData){
        return null
    }
    const columns: ColumnOrderTableSchema<ProductExtendedTyInputTableListing & SellOrderProductModelTypeExtended> = [
        {
            id: 'selected',
            dataKey: 'selected',
            label: '',
            simpleLabel: t('selectLine', 'Seleccionar línea'),
            width: 50,
            requiredColumn: true,
            component: 'ITCheckbox',
            headerClassName: 'checkbox_header_filter',
            searchableComponent: {
                component: 'SearchableComponentsCustomSelect',
                extraComponentData: { items: { 'unprocessed': { name: t('unprocessed', 'Sin procesar') }, 'processed': { name: t('processed', 'Procesados') } }, label: t('show', 'Mostrar') },
            }
        },
        {
            id: 'nLine',
            dataKey: 'nLine',
            label: t('line', 'Línea'),
            width: 70,
            hideColumnByDefault: true
        },
        {
            id: 'url',
            dataKey: 'url',
            label: t('image', 'Imagen'),
            component: 'ITDisplayImage',
            width: 70
        },
        {
            id: 'code_bar',
            dataKey: 'code_bar',
            label: t('codeBarShort', 'Cod. de barras'),
            simpleLabel: t('codeBar', 'Código de barras'),
            component: 'OrderSearchProductInputCreate',
            field: 'code_bar',
            typeOnChange: 'onChangeAdd',
            sortable: true,
            hideColumnByDefault: true,
            minWidth: 115
        },
        {
            id: 'reference',
            dataKey: 'reference',
            label: t('reference', 'Referencia'),
            component: 'OrderSearchProductInput',
            field: 'reference',
            sortable: true,
            minWidth: 100,
            hideColumnByDefault: true,
            className: 'quote_order_reference_name'
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            component: 'OrderSearchProductInputName',
            field: 'name',
            sortable: true,
            hideColumnByDefault: true,
            className: 'quote_order_reference_name'
        },
        {
            id: 'referenceAndName',
            dataKey: 'name',
            label: t('referenceName', 'Referencia y nombre'),
            component: 'OrderSearchProductInputReferenceAndName',
            field: 'referenceAndName',
            sortable: true,
            minWidth: 180,
            className: 'quote_order_reference_name'
        },
        {
            id: 'editProduct',
            dataKey: 'ProductId',
            label: '',
            simpleLabel: t('buttonSeeProduct', 'Botón de ver producto'),
            component: 'OrderEditProduct',
            width: 35,
            havePermission: permissionCheck(getUserPermissions('product.profile.main'), 'read'),
        },
        {
            id: 'descriptionDocuments',
            dataKey: 'descriptionDocuments',
            label: t('description', 'Descripción'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} className='twin_elipsis' />,
            hideColumnByDefault: true
        },
        {
            id: 'shortDescriptionDocuments',
            dataKey: 'shortDescriptionDocuments',
            label: t('shortDescriptionS', 'Desc. corta'),
            simpleLabel: t('shortDescription', 'Descripción corta'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'notes',
            dataKey: 'notes',
            label: t('notes', 'Notas'),
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue} className='twin_elipsis' />
        },
        {
            id: 'SpecificPriceRuleId',
            dataKey: 'SpecificPriceRuleId',
            label: t('priceRule', 'Tarifa'),
            customRender: (parameterValue, allRowData) => <CustomRenderString value={'T' + allRowData?.['SpecificPriceRule.id'] + ' - ' + allRowData?.['SpecificPriceRule.name']} className='twin_elipsis' />,
            hideColumnByDefault: true
        },
        {
            id: 'initialUnitPriceWithoutTax',
            dataKey: 'initialUnitPriceWithoutTax',
            label: () => <div className='text-right mr-15 w-full'>{t('price', 'Precio')}</div>,
            simpleLabel: t('price', 'Precio'),
            component: 'ITDisplayPrice',
            width: 100,
        },
        {
            id: 'manualPrice',
            dataKey: 'manualPrice',
            label: t('manualPriceS', 'P. man.'),
            simpleLabel: t('manualPrice', 'Precio manual'),
            toolTip: t('manualPrice', 'Precio manual'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={60} />,
            hideColumnByDefault: true,
            width: 85,
        },
        {
            id: 'quantity',
            dataKey: 'quantity',
            label: t('quantity', 'Cantidad'),
            width: 120,
            component: 'ITInputQuantity',
            applyMaxValue: true,
            readOnlyFnc: () => true
        },
        {
            id: 'quantity2',
            dataKey: 'quantity2',
            label: t('quantitySecondRes', 'Cant. sec.'),
            simpleLabel: t('quantitySecondary', 'Cantidad secundaria'),
            width: 90,
            customRender: (parameterValue, allRowData) => <ITTypeQuantityDisplay measure={allRowData.typeQuantity2} value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'wasteWeight',
            dataKey: 'wasteWeight',
            label: t('wasteWeight', 'Merma'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue ? parameterValue + ' %' : ''} />,
            width: 100,
            hideColumnByDefault: true,
        },
        {
            id: 'calculatedQuantity',
            dataKey: 'calculatedQuantity',
            label: t('quantityCalculatedS', 'Cant. calc.'),
            toolTip: t('quantityCalculated', 'Cantidad calculada'),
            simpleLabel: t('quantityCalculated', 'Cantidad calculada'),
            width: 120,
            component: 'ITInputQuantity',
            applyMaxValue: true,
            hideColumnByDefault: true,
            readOnlyFnc: () => true
        },
        {
            id: 'OrderAccountId',
            dataKey: 'OrderAccountId',
            label: t('chapter', 'Capítulo'),
            customRender: (parameterValue) => <CustomRenderString value={accounts[parameterValue]?.name || ''} className='twin_elipsis' />,
            hideColumnByDefault: true
        },
        {
            id: 'processed',
            dataKey: 'processed',
            component: 'OrderSaveRemainingProduct',
            label: t('invoiced', 'Facturado'),
            width: 110,
            forceNullBoolean: true
        },
        {
            id: 'discount',
            dataKey: 'discount',
            label: t('discount', 'Descuento'),
            width: 90,
            customRender: (parameterValue, allRowData) => <ITDiscountDisplay CurrencyId={completeData?.header.CurrencyId} value={parameterValue} typeDiscount={allRowData.typeDiscount} />
        },
        {
            id: 'rowPriceWithoutTaxPrev',
            dataKey: 'rowPriceWithoutTaxPrev',
            label: () => <div className='text-right mr-15 w-full'>{t('rowPrice', 'Precio Línea')}</div>,
            simpleLabel: t('rowPrice', 'Precio Línea'),
            component: 'ITDisplayFinalPrice',
            hideColumnByDefault: true,
            with_vat: false,
            width: 120
        },
        {
            id: 'rowPriceWithoutTaxAndTotalDiscount',
            dataKey: 'rowPriceWithoutTaxAndTotalDiscount',
            label: () => <div className='text-right w-full'>{t('priceTotal', 'Precio Total')}</div>,
            simpleLabel: t('priceTotal', 'Precio Total'),
            component: 'ITDisplayFinalPrice',
            with_vat: false,
            width: 120,
        },
        {
            id: 'rowPriceWithTaxAndTotalDiscount',
            dataKey: 'rowPriceWithTaxAndTotalDiscount',
            label: () => <div className='text-right w-full'>{t('totalPriceWithTaxShort', 'Precio Total Imp.')}</div>,
            simpleLabel: t('totalPriceWithTaxShort', 'Precio Total Imp.'),
            component: 'ITDisplayFinalPrice',
            with_vat: false,
            width: 130,
            hideColumnByDefault: true,
        },
        {
            id: 'totalWithTaxWithComplements',
            dataKey: 'totalWithTaxWithComplements',
            simpleLabel: t('totalWithComplementsAndVat', 'Total con complementos e imp.'),
            label: () => <div className='text-right w-full' >{t('withComplementsAndVatS', 'Con comp. e imp.')}</div>,
            component: 'ITDisplayPrice',
            width: 160,
            hideColumnByDefault: true,
            toolTip: t('totalWithComplementsAndVatL', 'Total con complementos e impuestos'),
        },
        {
            id: 'totalWithoutTaxWithComplements',
            dataKey: 'totalWithoutTaxWithComplements',
            simpleLabel: t('totalWithComplements', 'Total con complementos'),
            label: () => <div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('totalWithComplements', 'Total con complementos')
        },
        {
            id: 'totalWithoutTaxWithComplementsPrev',
            dataKey: 'totalWithoutTaxWithComplementsPrev',
            simpleLabel: t('totalWithoutTaxWithComplementsPrev', 'Total con complementos antes de desc.'),
            label: () => <div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('totalWithoutTaxWithComplementsPrev', 'Total con complementos antes de descuentos'),
        },
        {
            id: 'unitPriceWithComplements',
            dataKey: 'unitPriceWithComplements',
            simpleLabel: t('unitPriceWithComplements', 'Precio unitario con complementos'),
            label: () => <div className='text-right w-full'> {t('unitPricewithComplementsS', 'P. U. comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('unitPriceWithComplements', 'Precio unitario con complementos'),
        },
        {
            id: 'WarehouseId',
            dataKey: 'WarehouseId',
            label: t('warehouse', 'Almacén'),
            component: 'ITOrderWarehouse',
            hideColumnByDefault: true,
            warehouses: dictWarehouses || []
        },
        {
            id: 'unique_number',
            dataKey: 'unique_number',
            label: t('uniqueNumberShort', 'Nº único'),
            simpleLabel: t('uniqueNumber', 'Número único'),
            toolTip: t('uniqueNumber', 'Número único'),
            component: 'OrderUniqueNumberProductReadOnly',
            hideColumnByDefault: true,
        },
        {
            id: 'haveWorkSheetProductStatus',
            dataKey: 'haveWorkSheetProductStatus',
            label: '',
            width: 33,
            simpleLabel: t('workSheetStatus', 'Estado hoja de trabajo'),
            component: 'OrderProductWorkSheetStatus',
            hideColumnByDefault: true,
            Order: completeData?.header || {}
        },
    ]
    return (
        <OrderSellLayout modelId='SellDeliveryNoteId' translations={{ headerTitle: t('generateMultiDeliveryNote', 'Generar albarán múltiple') }} hideNotes={true} hideLeftFooter={true} name='CreateMultiSellDeliveryNote' orderStatusDict={sellInvoiceStatusInversed} buttonSave={
            permissionCheck(getUserPermissions('sell.deliveryNote'), 'create')
                ? <MultiSellButtonWithStock translateText={{
                    titleText: t('createUniqueDeliveryNoteFromSelectedProducts', 'Crear albarán único de los productos seleccionados'),
                    conflictText: t('conflictOrderSelectInfo', 'Existe un conflicto entre algunos de los pedidos seleccionados, debes decidir como quieres que sea el albarán que vas a crear.'),
                    confirmText: t('youSureAboutDeliveryNoteCreation', '¿Estás seguro que quieres crear un albarán con los productos seleccionados?')
                }} urlProcess='/api/app/order/sell/sellOrder/createSellDeliveryNoteFromMultiSellOrder' urlNavigate={locations.sellDeliveryNoteSingle} data={completeData} sellInfo={completeData?.header || {}} title={t('createDeliveryNoteWithSelected', 'Crear albarán con los seleccionados')} {...{ conflicts, setConflicts, getCompleteData }} />
                : undefined
        } InputTableHeaderChildren={MultiSellHeaderConnect} isReadOnly={true} ITHeaderChildrenExtraData={{ completeData, conflicts, setConflicts, urlNavigate: locations.sellInvoiceMulti, translateText: { titleTab: t('dataOrder', 'Datos del pedido'), textConflict: t('existConflictSellOrders', 'Existe conflicto entre pedidos') } }} {...{ columns, inputTableURLs, completeData, setCompleteData, getCompleteData, tableConfiguration, setTableConfiguration, orderHeaderURLs, userPermissions, title: t('multiSellDeliveryNote', 'Albarán múltiple') }}
        >
        </OrderSellLayout>
    )
}


export default CreateMultiSellDeliveryNotes